import { Col, Container, Row } from "design-react-kit";
import { get } from "lodash";
import moment from "moment";
import React, { lazy, Suspense, useCallback, useMemo } from "react";
import { useTranslate } from "react-admin";
import Breadcrumbs from "../../components/Breadcrumbs";
import Loader from "../../components/Loader";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import usePageGetPost from "../../pages/utils/usePageGetPost";
import EmptyMessage from "./EmptyMessage";
import LeftNavigator from "./LeftNavigator";
import { useNodePage } from "./NodePageContext";
import { CasaDiVetroProvider } from "./content-types/casaDiVetro/ContextCasaDiVetro";
import TableRowsCasaDiVetro from "./content-types/casaDiVetro/TableRowsCasaDiVetro";

const SimpleBlocks = lazy(() => import("./content-types/SimpleBlocks"));
const TableRows = lazy(() => import("./content-types/TableRows"));
const Tree = lazy(() => import("./content-types/Tree"));
const Nodes = lazy(() => import("./Nodes"));
const Roots = lazy(() => import("../home/Roots"));

const NodePage = (props) => {
  const translate = useTranslate();
  const { newParams } = useNodePage();
  const { uuid } = newParams;
  const dateFormat = useMemo(
    () => translate("website.date.format.short"),
    [translate],
  );

  const page = usePageGetPost("node", { ...newParams }, false);

  const pageData = useMemo(() => {
    if (page === false) return null;

    return {
      children: get(page, "children", []),
      contents: get(page, "contents", []),
      totalRows: get(page, "totalContents", []),
      node: get(page, "node"),
      contentType: get(page, "node.content_type", "node"),
      allowedContentType: get(page, "node.allowed_content_type"),
      roots: get(page, "roots"),
      description: get(page, "node.description", "website.navbar.node"),
      success: get(page, "success", true),
      breadcrumbs: get(page, "breadcrumbs", []),
      expanded: get(page, "expanded"),
    };
  }, [page]);

  const breadcrumbs = useMemo(() => {
    if (!pageData) return [];

    return [{ to: "/", label: "website.navbar.home" }].concat(
      pageData.breadcrumbs.map((item) => ({
        to: `/node?uuid=${get(item, "uuid")}`,
        label: get(item, "name"),
      })),
    );
  }, [pageData]);

  const textContent = useMemo(() => {
    if (!pageData?.description) return "";
    if (pageData?.description?.length === 1 && pageData?.description === "-")
      return "";
    return pageData.description.replace(/<[^>]+>/g, "").trim();
  }, [pageData?.description]);

  const formattedDates = useMemo(() => {
    if (!pageData?.node) return {};

    const { node } = pageData;
    return {
      published: node.published
        ? moment(node.published).format(dateFormat)
        : null,
      modified: node.modified ? moment(node.modified).format(dateFormat) : null,
    };
  }, [pageData?.node, dateFormat]);

  const renderLawReferences = useCallback(() => {
    if (!pageData?.node?.law_references?.length) return null;

    return pageData.node.law_references.map((lr, i) => (
      <a
        key={i}
        rel="noopener noreferrer"
        target="_blank"
        style={{ textDecoration: "underline", marginRight: 8 }}
        href={get(lr, "href", "#")}
      >
        {get(lr, "label", "")}
      </a>
    ));
  }, [pageData?.node?.law_references]);

  const shouldShowDescription = Boolean(pageData?.description && textContent);
  const shouldShowContent = pageData?.allowedContentType !== "node";
  const shouldShowSimpleBlocks =
    pageData?.allowedContentType === "simple_block" ||
    pageData?.contentType === "simple_block";
  const shouldShowTableRows = [
    "simple_table",
    "filterable_table",
    "node",
  ].includes(pageData?.allowedContentType);
  const shouldShowTree = pageData?.allowedContentType === "tree";
  const shouldShowCDV = pageData?.allowedContentType?.toLowerCase() === "csv";
  const shouldShowNodes = pageData?.allowedContentType !== "tree";
  const shouldShowEmptyMessage =
    pageData?.contentType === "node" &&
    !shouldShowCDV &&
    pageData?.contents.length === 0 &&
    pageData?.children.length === 0;
  const shouldShowDates =
    pageData?.node && (pageData.node.published || pageData.node.modified);
  const titleSection =
    pageData?.breadcrumbs[pageData?.breadcrumbs?.length - 1]?.name;

  if (page === false) {
    return <Loader />;
  }

  if (!pageData) {
    return <Loader />;
  }

  if (!pageData.success) {
    return (
      <>
        <Breadcrumbs {...props} items={breadcrumbs} />
        <Section className="pt-0">
          <SectionTitle
            title={get(pageData.node, "name", "website.navbar.node")}
          />
          <Container tag="div">
            <Row className="justify-content-center">
              <Col lg={8}>
                <br />
                <EmptyMessage uuid={uuid} />
              </Col>
            </Row>
          </Container>
        </Section>
      </>
    );
  }

  return (
    <>
      <div className="navigationContainer">
        <Container tag="div">
          <Row>
            <Col sm={12}>
              <Breadcrumbs {...props} items={breadcrumbs} />
              <SectionTitle
                title={get(pageData.node, "name", "website.navbar.node")}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <Container className="pt-0 px-4">
        <Row style={{ gap: "24px" }} className="pt-3">
          <Col
            style={{ borderBottom: "4px solid var(--accent) !important" }}
            className="mt-3 card card-bg"
            lg={3}
          >
            <LeftNavigator
              roots={pageData.roots}
              breadcrumbs={pageData.breadcrumbs}
              expanded={pageData.expanded}
            />
          </Col>
          <Col
            style={{ borderBottom: "4px solid var(--accent) !important" }}
            className="mt-3 card card-bg p-3"
            lg
          >
            <Container>
              <Row tag="div" className="px-3 mb-3">
                <Col xs="12">
                  <div className="section-title-container">
                    <h3>{titleSection}</h3>
                  </div>
                </Col>
              </Row>
            </Container>
            {shouldShowDescription && (
              <Container>
                <Row tag="div" className="px-3 mb-3">
                  <Col xs="12">
                    <Row>
                      <Col
                        dangerouslySetInnerHTML={{
                          __html: pageData.description,
                        }}
                      />
                    </Row>
                  </Col>
                </Row>
              </Container>
            )}
            {shouldShowContent && (
              <Container>
                {pageData.contentType !== "simple_block" && (
                  <Row tag="div" className="px-3">
                    <Col lg={12}>
                      <small>{renderLawReferences()}</small>
                      {pageData.node?.redirect_url && (
                        <h4 className="redirect-url">
                          <a
                            href={pageData.node.redirect_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {pageData.node.redirect_title} &rarr;
                          </a>
                        </h4>
                      )}
                    </Col>
                  </Row>
                )}

                <Suspense fallback={<Loader />}>
                  {shouldShowSimpleBlocks && (
                    <SimpleBlocks
                      className={!shouldShowDescription ? "px-3 mt-3" : "px-3"}
                      totalRows={pageData.totalRows}
                      data={pageData.contents}
                    />
                  )}

                  {shouldShowCDV && (
                    <CasaDiVetroProvider>
                      <TableRowsCasaDiVetro node={pageData.node} />
                    </CasaDiVetroProvider>
                  )}

                  {shouldShowTableRows && (
                    <TableRows
                      totalRows={pageData.totalRows}
                      data={pageData.contents}
                      node={pageData.node}
                    />
                  )}

                  {shouldShowTree && <Tree data={pageData.contents} />}
                </Suspense>
              </Container>
            )}

            {shouldShowNodes && (
              <Suspense fallback={null}>
                <Nodes uuid={uuid} items={pageData.children} />
              </Suspense>
            )}

            {shouldShowEmptyMessage && (
              <EmptyMessage node={pageData.node} className="px-4 pt-4" />
            )}

            {shouldShowDates && (
              <Container>
                <Row tag="div" className="px-3 pt-3">
                  <Col>
                    {formattedDates.published &&
                      translate("pages.node.published", {
                        date: formattedDates.published,
                      })}
                    {formattedDates.modified &&
                      formattedDates.published &&
                      " | "}
                    {formattedDates.modified &&
                      translate("pages.node.modified", {
                        date: formattedDates.modified,
                      })}
                    <br />
                  </Col>
                </Row>
              </Container>
            )}
          </Col>
        </Row>
      </Container>

      <Container className="pt-4 pb-4">
        <Suspense fallback={<Loader />}>
          <Roots roots={pageData.roots} />
        </Suspense>
      </Container>
    </>
  );
};

export default React.memo(NodePage);
